import { Nav } from "../components/Nav";
import { Logo } from "../components/Logo";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Mail, ArrowRight } from "lucide-react";

const platforms = [
    "SocialPulse", "FoodPulse", "NaturePulse", "IndianFamilyTree", "MyFamilyTree",
    "Weather Almanac", "Nature Museum", "Celebrate a Life", "Community directories",
    "Local business listings", "Future SocialPulse services",
];

const linkTargets = [
    "Business profiles", "Product pages", "Restaurant menus", "Promotional offers",
    "Coupons", "Events", "Community pages", "Family Tree pages", "Memorial pages",
    "NaturePulse articles", "FoodPulse products and recipes", "Seasonal promotions",
    "Landing pages", "Videos", "Maps", "Contact information", "Appointment booking pages", "Online stores",
];

const qrSurfaces = [
    "Restaurant tables", "Business cards", "Product packaging", "Storefront windows",
    "Flyers", "Posters", "Menus", "Brochures", "Event banners", "Vehicle decals",
    "Magazine advertisements", "Billboards", "Trade show booths", "Business invoices", "Product labels",
];

const offers = [
    "Daily specials", "Weekend deals", "Festival offers", "Holiday promotions",
    "Seasonal discounts", "New product launches", "Clearance sales", "Flash sales",
    "Loyalty rewards", "Coupon campaigns",
];

const analyticsBullets = [
    "Number of clicks", "QR code scans", "Geographic regions", "Device types",
    "Browser information", "Operating systems", "Referral sources", "Campaign performance",
    "Popular landing pages", "Time-based traffic trends",
];

const security = [
    "Secure HTTPS links", "Spam protection", "Malware screening", "Link management dashboard",
    "Custom aliases", "Link expiration", "Password-protected links", "Redirect management",
    "Access controls", "Administrative oversight", "Abuse monitoring",
];

const orgs = [
    "Small businesses", "Restaurants", "Retail stores", "Hotels",
    "Community organizations", "Schools", "Charities", "Nonprofits",
    "Religious institutions", "Tourism organizations", "Healthcare providers",
    "Event organizers", "Farmers", "Food producers", "Government agencies",
];

const businessProfileItems = [
    "Business information", "Contact details", "Maps and directions", "Hours of operation",
    "Products", "Services", "Photo galleries", "Videos", "Reviews", "Events",
    "Promotions", "News updates",
];

const futureFeatures = [
    "AI-powered marketing insights", "Dynamic QR codes", "Smart campaign management",
    "Team collaboration", "API integrations", "Bulk link creation", "Event tracking",
    "Location-aware redirects", "Personalized landing experiences", "Enterprise analytics",
    "Multi-language support", "Partner management tools",
];

function Chip({ children }) {
    return (
        <span className="inline-flex items-center border border-border bg-card px-3 py-1.5 text-sm text-foreground/90">
            {children}
        </span>
    );
}

function Section({ eyebrow, title, children, testId }) {
    return (
        <section className="border-b border-border" data-testid={testId}>
            <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                        {eyebrow && (
                            <div className="text-xs uppercase tracking-[0.25em] text-[hsl(217,91%,45%)]">
                                {eyebrow}
                            </div>
                        )}
                        <h2 className="mt-3 font-display text-3xl font-bold tracking-tighter sm:text-4xl">
                            {title}
                        </h2>
                    </div>
                    <div className="lg:col-span-8">{children}</div>
                </div>
            </div>
        </section>
    );
}

export default function About() {
    return (
        <div className="App min-h-screen">
            <Nav />

            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border">
                <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" aria-hidden />
                <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-[hsl(217,91%,60%)]/10 blur-3xl" aria-hidden />
                <div className="pointer-events-none absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-[hsl(100,76%,42%)]/10 blur-3xl" aria-hidden />
                <div className="relative mx-auto max-w-6xl px-6 pb-16 pt-24 sm:pt-28">
                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium uppercase tracking-widest text-muted-foreground" data-testid="about-badge">
                        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(100,76%,42%)]" />
                        About SpLink.pro™
                    </div>
                    <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="about-hero-heading">
                        Smart URL Shortening<br />
                        &amp; QR Codes for the<br />
                        <span className="brand-gradient-text">SocialPulse® and FoodPulse™</span> ecosystem.
                    </h1>
                    <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">
                        <strong className="text-foreground">Shorten. Share. Connect.</strong> In today's digital world, every click matters. Long, complicated web addresses are difficult to remember, hard to share, and often reduce the effectiveness of marketing campaigns. SpLink.pro™ is the official URL shortening and QR code platform within the SocialPulse ecosystem — designed to help businesses, organizations, creators, and community members share information quickly, professionally, and intelligently.
                    </p>
                    <p className="mt-4 max-w-3xl text-lg leading-relaxed text-muted-foreground">
                        More than just a URL shortener, SpLink.pro connects people with businesses, products, services, events, offers, and community resources across the growing family of SocialPulse websites.
                    </p>
                </div>
            </section>

            {/* What is SpLink.pro */}
            <Section eyebrow="Overview" title="What is SpLink.pro™?" testId="section-what-is">
                <p className="text-base leading-relaxed text-muted-foreground">
                    SpLink.pro™ is a secure URL management platform that transforms long website addresses into short, memorable links while automatically generating high-quality QR codes for both digital and printed marketing.
                </p>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    Every business partner participating in the SocialPulse ecosystem can create branded short links that direct visitors to:
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                    {linkTargets.map((t) => <Chip key={t}>{t}</Chip>)}
                </div>
                <p className="mt-6 font-display text-lg font-semibold text-foreground">
                    One simple link can connect customers to everything they need.
                </p>
            </Section>

            {/* Built for the network */}
            <Section eyebrow="Ecosystem" title="Built for the SocialPulse network" testId="section-network">
                <p className="text-base leading-relaxed text-muted-foreground">
                    SpLink.pro is fully integrated with the SocialPulse platform, allowing every participating website and partner to benefit from a unified linking system.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                    {platforms.map((p) => <Chip key={p}>{p}</Chip>)}
                </div>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                    Instead of sharing lengthy URLs, users can simply share a professional SpLink.pro link or QR code.
                </p>
            </Section>

            {/* QR codes */}
            <Section eyebrow="QR" title="QR codes for every business" testId="section-qr">
                <p className="text-base leading-relaxed text-muted-foreground">
                    Every shortened link automatically receives its own QR code. Businesses can place QR codes on:
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                    {qrSurfaces.map((s) => <Chip key={s}>{s}</Chip>)}
                </div>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                    Customers simply scan the QR code with their smartphone to instantly access the desired page.
                </p>
            </Section>

            {/* Offers */}
            <Section eyebrow="Marketing" title="Promote offers instantly" testId="section-offers">
                <p className="text-base leading-relaxed text-muted-foreground">
                    Businesses frequently launch promotions that change every week or every month. With SpLink.pro they can promote:
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                    {offers.map((o) => <Chip key={o}>{o}</Chip>)}
                </div>
                <p className="mt-6 rounded-md border-l-4 border-[hsl(100,76%,42%)] bg-card p-4 text-base leading-relaxed text-foreground">
                    Instead of reprinting marketing materials, businesses can update the destination page while keeping the same QR code and short URL.
                </p>
            </Section>

            {/* Analytics */}
            <Section eyebrow="Analytics" title="Better marketing analytics" testId="section-analytics">
                <p className="text-base leading-relaxed text-muted-foreground">
                    Every shortened URL can provide valuable insights into customer engagement. Depending on the subscription level, analytics may include:
                </p>
                <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {analyticsBullets.map((a) => (
                        <div key={a} className="flex items-center gap-3 border border-border bg-card px-4 py-3 text-sm">
                            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(217,91%,45%)]" />
                            {a}
                        </div>
                    ))}
                </div>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                    These insights help businesses improve marketing effectiveness and better understand customer behavior.
                </p>
            </Section>

            {/* Simple + memorable */}
            <Section eyebrow="Simplicity" title="Simple, professional, and memorable" testId="section-simple">
                <p className="text-base leading-relaxed text-muted-foreground">Instead of sharing:</p>
                <div className="mt-3 break-all rounded-md border border-border bg-muted/50 p-4 font-mono text-sm text-muted-foreground">
                    https://www.example.com/business/profile/products/category/seasonal-offers/summer-special-2026
                </div>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground">Businesses can simply share:</p>
                <div className="mt-3 flex flex-col gap-2">
                    <div className="inline-flex items-center gap-2 rounded-md border-2 border-[hsl(217,91%,45%)]/40 bg-card p-4 font-mono text-lg font-semibold text-[hsl(217,91%,45%)]">
                        splink.pro/summer
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-md border-2 border-[hsl(100,76%,42%)]/40 bg-card p-4 font-mono text-lg font-semibold text-[hsl(100,76%,42%)]">
                        splink.pro/abc123
                    </div>
                </div>
                <p className="mt-6 text-base text-muted-foreground">Short links are easier to:</p>
                <div className="mt-3 flex flex-wrap gap-2">
                    {["Remember", "Type", "Print", "Share on social media", "Include in advertisements", "Send through SMS", "Add to email signatures"].map((s) => <Chip key={s}>{s}</Chip>)}
                </div>
            </Section>

            {/* Security */}
            <Section eyebrow="Security" title="Secure link management" testId="section-security">
                <p className="text-base leading-relaxed text-muted-foreground">
                    Security is a core principle of SpLink.pro. Features include:
                </p>
                <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {security.map((s) => (
                        <div key={s} className="flex items-center gap-3 border border-border bg-card px-4 py-3 text-sm">
                            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(100,76%,42%)]" />
                            {s}
                        </div>
                    ))}
                </div>
            </Section>

            {/* Branded */}
            <Section eyebrow="Branding" title="Custom branded links" testId="section-branded">
                <p className="text-base leading-relaxed text-muted-foreground">
                    Business partners can create memorable branded URLs such as:
                </p>
                <div className="mt-4 flex flex-col gap-2 font-mono">
                    <div className="border border-border bg-card p-4 text-lg font-semibold text-primary">splink.pro/restaurant-name</div>
                    <div className="border border-border bg-card p-4 text-lg font-semibold text-primary">splink.pro/summer-sale</div>
                </div>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                    Branded links build trust while making marketing campaigns easier to recognize.
                </p>
            </Section>

            {/* Designed for every organization */}
            <Section eyebrow="Who it's for" title="Designed for every organization" testId="section-orgs">
                <p className="text-base leading-relaxed text-muted-foreground">SpLink.pro is suitable for:</p>
                <div className="mt-6 flex flex-wrap gap-2">
                    {orgs.map((o) => <Chip key={o}>{o}</Chip>)}
                </div>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                    Whether promoting a local event or a nationwide campaign, SpLink.pro provides a fast and reliable way to connect with audiences.
                </p>
            </Section>

            {/* Integrated */}
            <Section eyebrow="Integrations" title="Integrated with business profiles" testId="section-profiles">
                <p className="text-base leading-relaxed text-muted-foreground">
                    Every SocialPulse business partner can link directly to their official business profile. Customers can quickly access:
                </p>
                <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {businessProfileItems.map((b) => (
                        <div key={b} className="border border-border bg-card px-4 py-3 text-sm">
                            {b}
                        </div>
                    ))}
                </div>
                <p className="mt-6 font-display text-lg font-semibold">
                    Everything is just one click — or one scan — away.
                </p>
            </Section>

            {/* Local communities */}
            <Section eyebrow="Mission" title="Supporting local communities" testId="section-community">
                <p className="text-base leading-relaxed text-muted-foreground">
                    SpLink.pro is more than a technology platform. It supports the SocialPulse mission of connecting people with local businesses, community organizations, cultural institutions, educational resources, and public information.
                </p>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    By making information easier to access and share, SpLink.pro helps businesses reach more customers while strengthening local communities around the world.
                </p>
            </Section>

            {/* Future */}
            <Section eyebrow="Roadmap" title="The future of smart linking" testId="section-future">
                <p className="text-base leading-relaxed text-muted-foreground">
                    As the SocialPulse ecosystem continues to grow, SpLink.pro will expand with new capabilities including:
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                    {futureFeatures.map((f) => <Chip key={f}>{f}</Chip>)}
                </div>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                    These innovations will enable businesses of every size to market more effectively using intelligent, data-driven tools.
                </p>
            </Section>

            {/* Vision + Contact */}
            <section className="border-b border-border bg-[hsl(224,15%,7%)] text-[hsl(210,20%,98%)]" data-testid="section-vision">
                <div className="mx-auto max-w-6xl px-6 py-20">
                    <div className="text-xs uppercase tracking-widest text-[hsl(100,70%,55%)]">Our vision</div>
                    <h2 className="mt-3 font-display text-4xl font-bold tracking-tighter sm:text-5xl">
                        The trusted digital gateway<br />
                        for <span className="brand-gradient-text">SocialPulse® &amp; FoodPulse™</span>.
                    </h2>
                    <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[hsl(215,20%,80%)]">
                        SpLink.pro™ is the trusted digital gateway for the SocialPulse® and FoodPulse™ ecosystem — bringing together intelligent URL shortening, secure QR code generation, business promotion, and actionable analytics in one unified platform.
                    </p>
                    <p className="mt-4 max-w-3xl text-lg leading-relaxed text-[hsl(215,20%,80%)]">
                        Whether you're sharing a product, promoting an event, launching a campaign, or connecting customers to your business profile, SpLink.pro makes every link shorter, smarter, and more powerful.
                    </p>
                    <p className="mt-6 font-display text-2xl font-bold">
                        SpLink.pro™ — <span className="brand-gradient-text">Shorten. Share. Connect.</span>
                    </p>

                    <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <a
                            href="mailto:support@splink.pro"
                            className="group flex items-center justify-between border border-[hsl(224,12%,22%)] bg-[hsl(224,12%,10%)] p-6 transition-[background] hover:bg-[hsl(224,12%,14%)]"
                            data-testid="contact-email-link"
                        >
                            <div>
                                <div className="text-xs uppercase tracking-widest text-[hsl(215,20%,55%)]">Contact us</div>
                                <div className="mt-2 font-mono text-lg font-semibold">support@splink.pro</div>
                            </div>
                            <Mail className="h-6 w-6 text-[hsl(100,70%,55%)]" strokeWidth={1.5} />
                        </a>
                        <Link
                            to="/register"
                            className="group flex items-center justify-between border border-[hsl(224,12%,22%)] bg-[hsl(217,91%,45%)] p-6 transition-[background] hover:bg-[hsl(217,91%,50%)]"
                            data-testid="about-cta-register"
                        >
                            <div>
                                <div className="text-xs uppercase tracking-widest text-white/70">Get started free</div>
                                <div className="mt-2 font-display text-lg font-bold text-white">Create your first short link</div>
                            </div>
                            <ArrowRight className="h-6 w-6 text-white" strokeWidth={1.5} />
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="mx-auto max-w-6xl px-6 py-12">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <Logo />
                    <div className="flex flex-wrap items-center gap-6 font-mono text-xs text-muted-foreground">
                        <Link to="/about" className="hover:text-foreground">About</Link>
                        <Link to="/terms" className="hover:text-foreground">Terms</Link>
                        <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
                        <Link to="/cookies" className="hover:text-foreground">Cookies</Link>
                        <Link to="/dpa" className="hover:text-foreground">DPA</Link>
                        <span>© {new Date().getFullYear()} SpLink.pro™ · a SocialPulse® service</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
